import {
  LucideBell,
  LucideBellOff,
  LucideReplace,
  LucideTrash2,
} from 'lucide-react'
import { useCallback, useState } from 'react'
import {
  aMapCurrentCount,
  aMapFilters,
  aMapViewState,
  useAtomValue,
} from '~/atoms'
import { ControlTooltip, LoadingCircle } from '~/components/common'
import { AlertDialog, Button, toast } from '~/components/ui'
import type { Search } from '~/models'
import useSearchLocation from './useSearchLocation'

interface ActionsProps {
  search: Search
  updateSearchData: (updater: (searches: Search[]) => Search[]) => void
}

export default function Actions({ search, updateSearchData }: ActionsProps) {
  const [isUpdatingNotify, setIsUpdatingNotify] = useState(false)

  const updateNotify = useCallback(
    async (notify: boolean) => {
      setIsUpdatingNotify(true)
      const response = await fetch('/api/actions/update-search', {
        body: JSON.stringify({ _id: search._id, notify }),
        method: 'POST',
      })
      const result = await response.json()
      if (result.success) {
        updateSearchData((previousSearches) => {
          return previousSearches.map((s) =>
            s._id === search._id ? { ...s, notify } : { ...s }
          )
        })
      } else {
        console.error('Erroring updating notify', result.error)
        toast({
          variant: 'destructive',
          title: 'Error Updating Search',
          description:
            'Please try again. If the problem persists, contact support.',
        })
      }
      setIsUpdatingNotify(false)
    },
    [search, updateSearchData]
  )

  return (
    <div className="mt-4 flex gap-2">
      <ControlTooltip
        side="bottom"
        text={
          search.notify ? 'Turn off notifications' : 'Turn on notifications'
        }>
        <Button
          variant="outline"
          size="xs"
          className="gap-2"
          disabled={isUpdatingNotify}
          onClick={() => updateNotify(!search.notify)}>
          {isUpdatingNotify ? (
            <LoadingCircle className="h-4 w-4" />
          ) : search.notify ? (
            <LucideBell className="h-4 w-4" />
          ) : (
            <LucideBellOff className="h-4 w-4" />
          )}
          Notifications
        </Button>
      </ControlTooltip>

      <UpdateSearchButton search={search} updateSearchData={updateSearchData} />
      <DeleteSearchButton search={search} updateSearchData={updateSearchData} />
    </div>
  )
}

function UpdateSearchButton({
  search,
  updateSearchData,
}: {
  search: Search
  updateSearchData: (updater: (searches: Search[]) => Search[]) => void
}) {
  const [isUpdating, setIsUpdating] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  const mapFilters = useAtomValue(aMapFilters)
  const currentMapCount = useAtomValue(aMapCurrentCount)
  const mapViewState = useAtomValue(aMapViewState)
  const searchLocation = useSearchLocation()

  const handleUpdate = useCallback(async () => {
    setIsUpdating(true)

    const data = {
      filters: mapFilters,
      location: searchLocation,
      listingsCount: currentMapCount!,
      lat: mapViewState?.lat,
      lng: mapViewState?.lng,
      z: mapViewState?.zoom,
    }

    const response = await fetch('/api/actions/update-search', {
      body: JSON.stringify({ _id: search._id, data }),
      method: 'PUT',
    })
    const result = await response.json()
    if (result.success) {
      // @ts-expect-error
      updateSearchData((previousSearches) => {
        return previousSearches.map((s) =>
          s._id === search._id ? { ...s, ...data } : { ...s }
        )
      })
    } else {
      console.error('Erroring updating search', result.error)
      toast({
        variant: 'destructive',
        title: 'Error Updating Search',
        description:
          'Please try again. If the problem persists, contact support.',
      })
    }
    setIsOpen(false)
    setIsUpdating(false)
  }, [
    search,
    mapFilters,
    searchLocation,
    currentMapCount,
    mapViewState,
    updateSearchData,
  ])

  return (
    <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
      <ControlTooltip side="bottom" text="Update search to current view">
        <AlertDialog.Trigger asChild>
          <Button variant="outline" size="xs" className="gap-2">
            <LucideReplace className="h-4 w-4" />
            Update
          </Button>
        </AlertDialog.Trigger>
      </ControlTooltip>
      <AlertDialog.Content>
        <AlertDialog.Header>
          <AlertDialog.Title>Update Search</AlertDialog.Title>
          <AlertDialog.Description>
            Are you sure you want to update this search to match your current
            map view and filters? This action cannot be undone.
          </AlertDialog.Description>
        </AlertDialog.Header>
        <AlertDialog.Footer>
          <AlertDialog.Cancel disabled={isUpdating}>Cancel</AlertDialog.Cancel>
          <AlertDialog.Action onClick={handleUpdate} disabled={isUpdating}>
            {isUpdating ? (
              <>
                <LoadingCircle className="mr-2 h-4 w-4" />
                Updating...
              </>
            ) : (
              'Update Search'
            )}
          </AlertDialog.Action>
        </AlertDialog.Footer>
      </AlertDialog.Content>
    </AlertDialog>
  )
}

function DeleteSearchButton({
  search,
  updateSearchData,
}: {
  search: Search
  updateSearchData: (updater: (searches: Search[]) => Search[]) => void
}) {
  const [isDeleting, setIsDeleting] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  const handleDelete = useCallback(async () => {
    setIsDeleting(true)
    const response = await fetch('/api/actions/update-search', {
      body: JSON.stringify({ _id: search._id }),
      method: 'DELETE',
    })
    const result = await response.json()
    if (result.success) {
      updateSearchData((previousSearches) => {
        return previousSearches.filter((s) => s._id !== search._id)
      })
    } else {
      console.error('Erroring deleting search', result.error)
      toast({
        variant: 'destructive',
        title: 'Error Deleting Search',
        description:
          'Please try again. If the problem persists, contact support.',
      })
    }
    setIsOpen(false)
    setIsDeleting(false)
  }, [search, updateSearchData])

  return (
    <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
      <ControlTooltip side="bottom" text="Delete search">
        <AlertDialog.Trigger asChild>
          <Button variant="outline" size="xs" className="gap-2">
            <LucideTrash2 className="h-4 w-4 text-destructive" />
            Delete
          </Button>
        </AlertDialog.Trigger>
      </ControlTooltip>
      <AlertDialog.Content>
        <AlertDialog.Header>
          <AlertDialog.Title>Delete Search</AlertDialog.Title>
          <AlertDialog.Description>
            Are you sure you want to delete "{search.name}"? This action cannot
            be undone and you will stop receiving notifications for this search.
          </AlertDialog.Description>
        </AlertDialog.Header>
        <AlertDialog.Footer>
          <AlertDialog.Cancel disabled={isDeleting}>Cancel</AlertDialog.Cancel>
          <AlertDialog.Action
            onClick={handleDelete}
            disabled={isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
            {isDeleting ? (
              <>
                <LoadingCircle className="mr-2 h-4 w-4" />
                Deleting...
              </>
            ) : (
              'Delete Search'
            )}
          </AlertDialog.Action>
        </AlertDialog.Footer>
      </AlertDialog.Content>
    </AlertDialog>
  )
}
